import { createApiService } from "@/core/api/apiService";
import { ApiError } from "@/core/api/apiTypes";
import type {
  CashPaymentDto, CreatePaymentDto, PaymentDetailsDto, PaymentDto, PaymentPreparationDto, PaymentSessionDto, PaymentSummaryDto,
  RecordCashPaymentDto, VoidPaymentDto,
} from "../types/paymentTypes";

async function readPayment<T>(request: () => Promise<T>, signal?: AbortSignal): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    signal?.throwIfAborted();
    try {
      return await request();
    } catch (error) {
      if (!(error instanceof ApiError) || error.Status !== 409 ||
        !error.Data || typeof error.Data !== "object" || !("code" in error.Data) ||
        error.Data.code !== "payment_status_pending" || attempt >= 9) throw error;
      await new Promise<void>((resolve, reject) => {
        const abort = () => {
          window.clearTimeout(timer);
          signal?.removeEventListener("abort", abort);
          reject(signal?.reason ?? new DOMException("Aborted", "AbortError"));
        };
        const timer = window.setTimeout(() => {
          signal?.removeEventListener("abort", abort);
          resolve();
        }, 3000);
        signal?.addEventListener("abort", abort, { once: true });
        if (signal?.aborted) abort();
      });
    }
  }
}

export function createPaymentApiService() {
  const owner = createApiService("businesses");
  const customer = createApiService("appointment-payment-sessions");
  const path = (businessId: string, appointmentId: string) =>
    `/${encodeURIComponent(businessId)}/appointments/${encodeURIComponent(appointmentId)}/payments`;
  const options = (signal?: AbortSignal) => ({ signal, cache: "no-store" as const, referrerPolicy: "no-referrer" as const });
  const publicOptions = (signal?: AbortSignal) => ({ ...options(signal), skipAuth: true, credentials: "omit" as const });
  return {
    getSummary: (businessId: string, appointmentId: string, signal?: AbortSignal) =>
      owner.get<PaymentSummaryDto>(path(businessId, appointmentId), options(signal)),
    create: (businessId: string, appointmentId: string, dto: CreatePaymentDto) =>
      owner.post<PaymentDto>(path(businessId, appointmentId), dto, options()),
    getById: (businessId: string, appointmentId: string, paymentId: string, signal?: AbortSignal) =>
      readPayment(() => owner.get<PaymentDto>(`${path(businessId, appointmentId)}/${encodeURIComponent(paymentId)}`, options(signal)), signal),
    cancel: (businessId: string, appointmentId: string, paymentId: string, idempotencyKey: string) =>
      owner.post<PaymentDto>(`${path(businessId, appointmentId)}/${encodeURIComponent(paymentId)}/cancel`,
        { idempotency_key: idempotencyKey }, options()),
    void: (businessId: string, appointmentId: string, paymentId: string, dto: VoidPaymentDto) =>
      owner.post<PaymentDto>(`${path(businessId, appointmentId)}/${encodeURIComponent(paymentId)}/void`, dto, options()),
    recordCash: (businessId: string, appointmentId: string, dto: RecordCashPaymentDto) =>
      owner.post<CashPaymentDto>(`${path(businessId, appointmentId)}/cash`, dto, options()),
    voidCash: (businessId: string, appointmentId: string, cashPaymentId: string) =>
      owner.post<CashPaymentDto>(`${path(businessId, appointmentId)}/cash/${encodeURIComponent(cashPaymentId)}/void`, {}, options()),
    createSession: (businessId: string, appointmentId: string, paymentId: string, idempotencyKey: string, signal?: AbortSignal) =>
      owner.post<PaymentSessionDto>(`${path(businessId, appointmentId)}/${encodeURIComponent(paymentId)}/customer-sessions`,
        { idempotency_key: idempotencyKey }, options(signal)),
    open: (token: string, signal?: AbortSignal) =>
      readPayment(() => customer.post<PaymentDetailsDto>("/open", { token }, publicOptions(signal)), signal),
    prepare: (token: string, signal?: AbortSignal) =>
      customer.post<PaymentPreparationDto>("/prepare", { token }, publicOptions(signal)),
    status: (token: string, signal?: AbortSignal) =>
      readPayment(() => customer.post<PaymentDto>("/status", { token }, publicOptions(signal)), signal),
  };
}
export const paymentApiService = createPaymentApiService();
